import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import TabNavigation from '../../components/TabNavigation';
import { useAlert } from '../../context/AlertContext';

const ScheduleAudit = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const preSelectedTemplate = location.state?.template;

  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scheduleForm, setScheduleForm] = useState({
    template_id: preSelectedTemplate?.id || '',
    template_name: preSelectedTemplate?.template_name || '',
    audit_title: '',
    auditor_id: '',
    auditor_name: '',
    scheduled_date: '',
    recurrence: '',
    location: '',
    department: ''
  });

  const recurrenceOptions = ['', 'Weekly', 'Monthly', 'Quarterly', 'Annual'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [templatesRes, usersRes] = await Promise.all([
        axios.get(`${API}/audit-templates`, { headers }),
        axios.get(`${API}/users`, { headers })
      ]);
      
      setTemplates(templatesRes.data.filter(t => t.is_active));
      setUsers(usersRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    const template = templates.find(t => t.id === templateId);
    
    setScheduleForm({
      ...scheduleForm,
      template_id: templateId,
      template_name: template?.template_name || '',
      audit_title: template ? `${template.template_name} - ${new Date().toLocaleDateString()}` : ''
    });
  };

  const handleAuditorChange = (e) => {
    const auditorId = e.target.value;
    const auditor = users.find(u => u.id === auditorId);
    
    setScheduleForm({
      ...scheduleForm,
      auditor_id: auditorId,
      auditor_name: auditor?.name || ''
    });
  };

  const handleSubmit = async () => {
    if (!scheduleForm.template_id || !scheduleForm.auditor_id || !scheduleForm.scheduled_date) {
      showWarning('Please fill in all required fields');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

      await axios.post(`${API}/audit-schedules`, scheduleForm, { headers });
      showSuccess('Audit scheduled successfully!');
      navigate('/audits');
    } catch (err) {
      console.error('Failed to schedule audit:', err);
      showError('Failed to schedule audit');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="p-6">
        <div className="mb-6">
          <button
            onClick={() => navigate('/audits')}
            className="text-blue-600 hover:underline mb-4 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Audits Dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-800">📅 Schedule Audit</h1>
          <p className="text-gray-600 mt-1">Plan a new audit or inspection</p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6 max-w-3xl">
          <div className="space-y-6">
            {/* Template Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Audit Template <span className="text-red-500">*</span>
              </label>
              <select
                value={scheduleForm.template_id}
                onChange={handleTemplateChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Select a template</option>
                {templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.template_name} - {template.audit_type}
                  </option>
                ))}
              </select>
            </div>

            {/* Audit Title */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Audit Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={scheduleForm.audit_title}
                onChange={(e) => setScheduleForm({...scheduleForm, audit_title: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                placeholder="e.g., Monthly Site Walk - January 2025"
              />
            </div>

            {/* Auditor Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Assign Auditor <span className="text-red-500">*</span>
              </label>
              <select
                value={scheduleForm.auditor_id}
                onChange={handleAuditorChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">Select an auditor</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.name} - {u.role}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Scheduled Date */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Scheduled Date & Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  value={scheduleForm.scheduled_date}
                  onChange={(e) => setScheduleForm({...scheduleForm, scheduled_date: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              {/* Recurrence */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Recurrence
                </label>
                <select
                  value={scheduleForm.recurrence}
                  onChange={(e) => setScheduleForm({...scheduleForm, recurrence: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  {recurrenceOptions.map(opt => (
                    <option key={opt} value={opt}>
                      {opt || 'One-time (No recurrence)'}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Location */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Location
                </label>
                <input
                  type="text"
                  value={scheduleForm.location}
                  onChange={(e) => setScheduleForm({...scheduleForm, location: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., Building A, Floor 3"
                />
              </div>

              {/* Department */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Department
                </label>
                <input
                  type="text"
                  value={scheduleForm.department}
                  onChange={(e) => setScheduleForm({...scheduleForm, department: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="e.g., Manufacturing"
                />
              </div>
            </div>

            {/* Info Box */}
            {scheduleForm.recurrence && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>ℹ️ Recurring Audit:</strong> This audit will be automatically scheduled {scheduleForm.recurrence.toLowerCase()} based on the initial date. You&apos;ll receive reminders 24 hours and 1 hour before each scheduled audit.
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={() => navigate('/audits')}
                className="flex-1 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!scheduleForm.template_id || !scheduleForm.auditor_id || !scheduleForm.scheduled_date}
                className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Schedule Audit
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
      </div>
  );
};

export default ScheduleAudit;
